// Types pour l'API Nubiecloud

export interface User {
  id: string;
  email: string;
  nom: string;
  prenom: string;
  date_creation: string;
  derniere_connexion?: string;
  is_verified: boolean;
  role: string;
  // `null` veut dire « jamais recueillie » -- un compte anterieur au dispositif. Ce
  // n'est PAS une acceptation : c'est ce qui fait apparaitre le bandeau.
  conditions_version_acceptee?: string | null;
  conditions_acceptees_le?: string | null;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
}

/**
 * Returned by /api/v1/auth/login when the user has MFA enabled. The cookie
 * is NOT yet posted — the caller must POST /auth/mfa/login-verify with the
 * mfa_token + a valid TOTP/backup code to complete authentication.
 */
export interface MfaLoginRequiredResponse {
  requires_mfa: true;
  mfa_token: string;
}

/**
 * Returned by /api/v1/auth/login when the user logs in from an untrusted device.
 * The cookie is NOT yet posted — the caller must POST /auth/device/verify with
 * the verify_token + the 6-digit code sent by email.
 */
export interface DeviceVerifyRequiredResponse {
  requires_device_verification: true;
  verify_token: string;
  email_hint?: string;
}

export type LoginResult = AuthResponse | MfaLoginRequiredResponse | DeviceVerifyRequiredResponse;

export const isMfaRequired = (r: LoginResult): r is MfaLoginRequiredResponse =>
  (r as MfaLoginRequiredResponse).requires_mfa === true;

export const isDeviceVerifyRequired = (r: LoginResult): r is DeviceVerifyRequiredResponse =>
  (r as DeviceVerifyRequiredResponse).requires_device_verification === true;

export interface TrustedDevice {
  id: string;
  label: string | null;
  ip_last: string | null;
  ua_last: string | null;
  last_seen_at: string;
  expires_at: string;
  is_current: boolean;
}

export interface TrustedDevicesListResponse {
  devices: TrustedDevice[];
}

export interface MfaEnrollResponse {
  enrollment_token: string;
  otpauth_uri: string;
  qr_data_url: string;
  backup_codes: string[];
}

export interface MfaStatus {
  enabled: boolean;
  enabled_at: string | null;
  backup_codes_remaining: number;
}

// ----- Connexion sociale (Google) et portes du compte ----------------------

export type IdentityProvider = 'password' | 'google' | 'github' | 'entra';

// ----- SSO d'une organisation (plan B palier 2) ------------------------------

export type LoginMethod = 'password' | 'google' | 'entra';
export type MfaPolicy = 'local' | 'idp';

export interface IdentityProviderConfig {
  id: string;
  organisation_id: string;
  kind: 'entra';
  tenant_id: string;
  display_name: string | null;
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
  redirect_uri: string;            // a coller dans l'App registration cote client
  platform_login_enabled: boolean; // ENTRA_LOGIN_ENABLED cote API
}

export interface IdentityProviderUpsert {
  tenant_id: string;
  display_name?: string | null;
  is_enabled: boolean;
}

export interface OrgDomain {
  id: string;
  organisation_id: string;
  domain: string;
  is_verified: boolean;
  verified_at: string | null;
  last_checked_at: string | null;
  check_failures: number;
  created_at: string;
  txt_record_name: string;
  txt_record_value: string;
}

export interface DomainVerifyResponse {
  domain: OrgDomain;
  found: boolean;
  detail: string | null; // dns_no_txt | dns_token_mismatch | dns_nxdomain | dns_timeout | dns_error
}

export interface LoginPolicy {
  organisation_id: string;
  methodes_connexion_autorisees: LoginMethod[];
  mfa_policy: MfaPolicy;
  verified_domains: string[];
  has_enabled_provider: boolean;
}

export interface LoginPolicyUpdate {
  methodes_connexion_autorisees?: LoginMethod[];
  mfa_policy?: MfaPolicy;
}

export interface ScimTokenResponse {
  token: string;          // montre UNE fois
  token_prefix: string;
  scim_base_url: string;
  created_at: string;
}

export interface ScimEvent {
  id: string;
  received_at: string;
  method: string;
  path: string;
  resource_type: string | null;
  resource_id: string | null;
  external_id: string | null;
  outcome: string;        // ok | created | updated | deactivated | reactivated | deleted | owner_protected | error | unauthorized
  status_code: number;
  detail: string | null;
  duration_ms: number | null;
}

export interface ScimSyncStatus {
  enabled: boolean;
  scim_base_url: string;
  token_prefix: string | null;
  token_created_at: string | null;
  last_seen_at: string | null;
  provisioned_users: number;
  synced_groups: number;
  last_24h: { hours: number; total: number; errors: number; unauthorized: number; by_outcome: Record<string, number> };
  recent_events: ScimEvent[];
}


// ---------------------------------------------------------------- Serveur MCP
// `destroy` N'EXISTE PAS : toute destruction se fait dans la console (decision D1 du
// plan MCP). Le type l'interdit, ce qui evite qu'un formulaire la propose un jour.
export type McpCapability = 'read' | 'write' | 'operate';

export interface McpToken {
  id: string;
  label: string | null;
  capabilities: McpCapability[];
  project_id: string | null;
  token_prefix: string;
  created_at: string;
  last_seen_at: string | null;
  revoked_at: string | null;
}

// La valeur n'est rendue QU'A LA CREATION : la base ne garde que son empreinte.
export interface McpTokenIssued extends McpToken {
  token: string;
  mcp_url: string;
}

// La saisie d'un secret demandee par un agent (elicitation en mode URL).
// Elle ne porte QUE des noms : ni les valeurs a poser -- elles n'existent pas encore --
// ni celles deja en place, qui ne ressortent jamais de git.
export interface McpSecretEntry {
  deployment_id: string;
  application: string;
  noms_demandes: string[];
  expire_dans_s: number;
  applique: boolean;
}

// L'approbation d'une operation demandee par un agent (MRTR en mode URL).
// Elle ne porte rien d'executable : la console enregistre une DECISION, et c'est l'outil
// MCP qui lance l'operation a son rejeu, apres verification de son sceau.
export type McpConfirmationEtat = 'en_attente' | 'approuvee' | 'refusee' | 'relevee';

export interface McpConfirmation {
  operation: string;
  deployment_id: string;
  message: string;
  resume: Record<string, string>;
  etat: McpConfirmationEtat;
  expire_dans_s: number;
}

export interface McpEvent {
  id: string;
  received_at: string;
  method: string;
  tool_name: string | null;
  outcome: string;
  status_code: number;
  detail: string | null;
  duration_ms: number | null;
}

export interface McpStatus {
  mcp_url: string;
  jetons_actifs: number;
  // Les sessions OAuth comptent a part : un proprietaire qui ne voit que les jetons
  // croit avoir coupe l'acces alors qu'il n'en a coupe qu'une moitie.
  sessions_actives: number;
  outils_disponibles: string[];
  last_24h: { hours: number; total: number; errors: number; unauthorized: number; by_outcome: Record<string, number>; by_tool: Record<string, number> };
  recent_events: McpEvent[];
}

export interface SsoDiscoverResponse {
  sso_available: boolean;
  provider: 'entra' | null;
  organisation_id: string | null;
  organisation_name: string | null;
  allowed_methods: LoginMethod[];
  sso_required: boolean;
}

export interface UserIdentity {
  id: string;
  provider: IdentityProvider;
  provider_tenant: string;
  email_at_link: string | null;
  created_at: string;
  last_login_at: string | null;
}

export interface IdentitiesResponse {
  identities: UserIdentity[];
  has_password: boolean;
}

export interface GoogleAuthorizeResponse {
  authorization_url: string;
}

/**
 * Renvoye par /auth/google/link/resend : un nouveau code a ete envoye, le
 * link_token precedent ne vaut plus rien.
 */
export interface GoogleLinkRequiredResponse {
  requires_link_confirmation: true;
  link_token: string;
  email_hint?: string;
}

export interface LoginRequest {
  email: string;
  mot_de_passe: string;
}

export interface UserCreate {
  nom: string;
  prenom: string;
  email: string;
  mot_de_passe: string;
  role?: string;
  // Version des conditions d'utilisation acceptee. L'API la REFUSE si elle manque
  // (422) : la case a cocher est une consigne du client, le refus serveur est la garde.
  conditions_version?: string;
}

export interface UserVerification {
  email: string;
  token?: string | null;
  verification_type?: string;
}

export interface VerificationResponse {
  message: string;
  user_id: string;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetResponse {
  message: string;
  email: string;
  expires_in_minutes?: number;
}

export interface PasswordResetConfirm {
  email: string;
  token: string;
  nouveau_mot_de_passe: string;
}

// Project Access Context Types (Feature 1: Organization Owner View)
export type ProjectRole = 'admin' | 'developer' | 'user';

export interface ProjectAccessContext {
  has_access: boolean;
  // group : acces par un groupe seulement ; org_admin : ADMIN de l'organisation sans adhesion directe
  access_type: 'member' | 'org_owner' | 'org_admin' | 'both' | 'group' | null;
  role_in_project: ProjectRole | null;   // role DIRECT (compat), null si herite
  is_org_owner: boolean;
  role_effective?: ProjectRole | null;   // max(direct, groupes, organisation)
  sources?: string[];                    // membre_direct | groupe:<nom> | owner_organisation | admin_organisation
  groups?: string[];
}

// ----- Groupes d'une organisation (plan B) ---------------------------------

export type GroupSource = 'manual' | 'entra';

export interface Group {
  id: string;
  organisation_id: string;
  nom: string;
  description: string | null;
  source: GroupSource;   // entra : composition en lecture seule (synchronisee)
  member_count: number;
  project_count: number;
  created_at: string;
}

export interface GroupCreate {
  nom: string;
  description?: string | null;
}

export interface GroupUpdate {
  nom?: string;
  description?: string | null;
}

export interface GroupMember {
  utilisateur_id: string;
  nom: string;
  prenom: string;
  email: string;
  source: GroupSource;
  date_ajout: string;
}

export interface GroupProject {
  projet_id: string;
  projet_nom: string;
  role_projet: ProjectRole;
}

export interface ProjectGroup {
  groupe_id: string;
  nom: string;
  source: GroupSource;
  role_projet: ProjectRole;
  member_count: number;
}

export type AccessSourceType = 'membre_direct' | 'groupe' | 'owner_organisation' | 'admin_organisation';

export interface AccessSource {
  type: AccessSourceType;
  groupe_nom?: string | null;
}

export interface ProjectEffectiveMember {
  utilisateur_id: string;
  nom: string;
  prenom: string;
  email: string;
  role_effective: ProjectRole;
  role_direct: ProjectRole | null;
  sources: AccessSource[];
  groups: string[];
}

export interface Project {
  id: string;
  nom: string;
  description?: string | null;
  quota_cpu: number;
  quota_ram: number;
  quota_stockage: number;
  organisation_id: string;
  nom_dossier_git: string;
  tenant_k8s_id: string;
  date_creation: string;
  access_context?: ProjectAccessContext; // NEW: Access context for UI badges
  resource_mode?: string;
  instance_type_id?: string | null;
  region_id?: string | null;
  region?: RegionInfo | null;
}

export interface ProjectCreate {
  nom: string;
  description?: string | null;
  quota_cpu?: number;
  quota_ram?: number;
  quota_stockage: number;
  resource_mode?: 'PRESET' | 'CUSTOM';
  instance_type_id?: string;
  organisation_id?: string;
  region_id?: string;
}

export interface Environment {
  id: string;
  nom: string;
  type: 'dev' | 'staging' | 'preproduction' | 'prod';
  projet_id: string;
  nom_dossier_git: string;
  namespace_k8s_id: string;
  date_creation: string;
}

export interface EnvironmentCreate {
  nom: string;
  type?: 'dev' | 'staging' | 'preproduction' | 'prod';
}

export interface EnvironmentUpdate {
  nom?: string;
  type?: 'dev' | 'staging' | 'preproduction' | 'prod';
}

export interface EnvironmentStatus {
  environment_id: string;
  name: string;
  namespace: string;
  status: string;
  gitlab_config: string;
  kubernetes_namespace: string;
  argocd_application: string;
  last_deployment: {
    id?: string;
    status?: string;
    created_at?: string;
    completed_at?: string;
  } | null;
  created_at: string;
}

export interface Deployment {
  id: string;
  environnement_id: string;
  nom: string;
  type_application: string;
  repo_url?: string | null;
  image_registry_url?: string | null;
  image_tag?: string | null;
  cpu_requis: number;
  ram_requise: number;
  storage_requis: number;
  replicas: number;
  certificat_tls_id?: string | null;
  nom_de_domaine?: string | null;
  helm_chart_version: string;
  statut_deploiement: string;
  date_creation: string;
  derniere_mise_a_jour?: string | null;
  application_url?: string | null;
  // Odoo-specific fields
  odoo_version?: string | null;
  odoo_language?: string | null;
  /** Keycloak : présent UNIQUEMENT dans la réponse de création. Ne jamais
   *  s'attendre à le retrouver dans une lecture ultérieure. */
  admin_credentials?: KeycloakAdminCredentials | null;
  /** Applications managées : la base dédiée créée sur le PostgreSQL hôte.
   *  Même règle : réponse de création uniquement. */
  database_credentials?: ManagedDatabaseCredentials | null;
}

/** Applications managées : identifiants de la base dédiée, renvoyés UNE SEULE
 *  FOIS à la création. Le mot de passe est ensuite stocké chiffré sur le
 *  PostgreSQL hôte et la console ne montre plus qu'un booléen `has_password`. */
export interface ManagedDatabaseCredentials {
  /** Alias interne vers le pooler du PostgreSQL hôte — celui que la fiche du
   *  PostgreSQL affiche comme `internal_host`. Joignable depuis l'environnement,
   *  pas depuis Internet. */
  host: string;
  port: string;
  name: string;
  username: string;
  password: string;
  /** Nom du déploiement PostgreSQL hôte, pour dire au client OÙ elle vit. */
  host_deployment_nom: string;
}

/** Keycloak : identifiants administrateur, renvoyés UNE SEULE FOIS à la création.
 *  Le mot de passe n'est stocké en clair nulle part côté API — aucune lecture
 *  ultérieure ne peut le réexposer. */
export interface KeycloakAdminCredentials {
  /** `admin` : le compte administrateur de l'application (Keycloak l'amorce
   *  depuis un secret). `protection` : un accès temporaire posé devant
   *  l'application, à retirer une fois l'installation faite — n8n et Metabase
   *  n'ont aucun compte tant qu'un humain n'a pas rempli leur assistant, et le
   *  premier qui ouvre l'URL le revendique. */
  kind?: 'admin' | 'protection';
  /** Absent pour une application qui s'authentifie avec le SEUL secret
   *  (Hasura) : afficher un identifiant enverrait le client chercher une
   *  saisie qui n'est pas sur son ecran. */
  username?: string;
  password: string;
  console_url: string;
}

/** Keycloak : où vivent ses données. Le lien vers le PostgreSQL hôte n'existe
 *  que dans la déclaration côté API — la console ne peut pas le deviner. */
export interface KeycloakDatabaseInfo {
  database_name: string;
  host_deployment_id: string | null;
  host_deployment_nom: string | null;
  destroyed_on_delete: boolean;
}

export interface KeycloakEligibleDatabase {
  deployment_id: string;
  nom: string;
  eligible: boolean;
  reason: string | null;
}

export interface KeycloakEligibleDatabases {
  environment_id: string;
  databases: KeycloakEligibleDatabase[];
  eligible_count: number;
}

export interface DeploymentDetail {
  deployment_id: string;
  application_name: string;
  type_application: string;
  is_managed_service: boolean;
  /** true = la suppression est refusee tant que la protection n'est pas levee. */
  deletion_protection?: boolean;
  /** Application fournie par la plateforme mais exposée (Keycloak). Distinct de
   *  is_managed_service : elle a une URL publique. */
  is_managed_application?: boolean;
  keycloak_database?: KeycloakDatabaseInfo | null;
  /** PostgreSQL managé : les Keycloak dont la base vit sur ce cluster. Liste
   *  vide = aucun ; absente = type non concerné ou résolution impossible.
   *  Sert à avertir avant une restauration (CNPG restaure le CLUSTER entier,
   *  donc aussi les royaumes du Keycloak) et avant une suppression. */
  /** Applications managées hébergées par ce PostgreSQL. Le TYPE accompagne le
   *  nom : une même base peut porter un Keycloak, un n8n et un Metabase, et
   *  l'avertissement doit nommer ce qu'elle héberge vraiment. */
  hosted_apps?: Array<{
    deployment_id: string;
    nom: string;
    type_application: string;
    label: string;
  }> | null;
  /** Applications à installation manuelle (n8n, Metabase) : état de l'accès
   *  temporaire posé devant l'application. Lu depuis git — c'est un état piloté
   *  par une opération, il n'est dérivable de rien. */
  // `removable` vient du SERVEUR, jamais d'une deduction locale : c'est la
  // meme source que la garde de l'endpoint, donc les deux ne peuvent pas
  // diverger. Cas vise : une application sans aucun systeme de comptes, ou
  // cette porte EST le controle d'acces -- le serveur refuse son retrait en 409.
  setup_protection?: { applicable: boolean; enabled: boolean; removable?: boolean } | null;
  status: string;
  environment_id: string;
  argocd: {
    app_name: string;
    health: string | null;
    sync: string | null;
  };
  image_url: string | null;
  image_tag: string | null;
  /** La SOURCE du deploiement : quel depot connecte il suit, sur quelle branche, et
   *  si l'auto-deploiement est actif. `connected_repository` est null quand aucune
   *  source n'est declaree -- distinct d'un depot dont la lecture a echoue, qui ne
   *  rend que `connected_repository_id`. */
  connected_repository_id?: number | null;
  connected_repository?: {
    id: number;
    full_name: string;
    repo_url: string;
    provider: string;
  } | null;
  git_branch?: string | null;
  auto_deploy_enabled?: boolean;
  repository_url: string | null;
  domain: string | null;
  application_url?: string | null;
  expose_public?: boolean;
  internal_host?: string | null;
  cpu_required: number;
  ram_required: number;
  storage_required: number;
  replicas: number;
  created_at: string;
  updated_at: string;
  odoo_version?: string | null;
  odoo_language?: string | null;
  // Variables d'environnement (CODE apps)
  environment_variables?: Record<string, string> | null;
  sensitive_variables?: Record<string, string> | null;
  // PostgreSQL managé : additional_databases masqué (passwords jamais renvoyés)
  additional_databases?: Array<{
    name: string;
    user?: string;
    owner?: string;
    has_password: boolean;
  }> | null;
  // Type de charge, commande de démarrage et planification. AUCUNE COLONNE EN BASE : la
  // fiche les relit du values.yaml en git, comme environment_variables. Les typer ici est
  // ce qui permet de pré-remplir l'écran « Modifier » sans `as any` — et sans ce
  // pré-remplissage, le premier enregistrement figerait la valeur.
  workload_type?: string | null;
  command?: string[] | null;
  schedule?: string | null;
  // Port et health check (source : colonnes en base) — typés ici pour la même raison.
  port?: number | null;
  health_check_path?: string | null;
  // Kubernetes identifiers (for Prometheus metrics)
  namespace?: string;
  tenant_id?: string;
}

export interface DeploymentUpdate {
  nom?: string;
  cpu_requis?: number;
  ram_requise?: number;
  storage_requis?: number;
  replicas?: number;
  image_registry_url?: string;
  image_tag?: string;
  nom_de_domaine?: string;
  certificat_tls_id?: string | null;
  helm_chart_version?: string;
  // Variables d'environnement (CODE apps)
  environment_variables?: Record<string, string>;
  sensitive_variables?: Record<string, string>;
  // Port de l'application (CODE apps)
  port?: number;
  // Health check path (CODE apps)
  health_check_path?: string;
  // Type de charge, commande, planification (CODE apps).
  // Sémantique côté API : champ absent = préserve ; `[]`/`''` = effacer (retour à
  // l'ENTRYPOINT de l'image) ; valeur = remplacer. Envoyer `'web'` EXPLICITEMENT est la
  // seule façon de repasser un worker en app web — le deep merge de git étant additif,
  // une clé omise survit dans le fichier.
  workload_type?: string;
  command?: string[] | null;
  schedule?: string;
  // Migration job (CODE apps)
  migration_enabled?: boolean;
  migration_command?: string[] | null;
  migration_block_on_failure?: boolean;
  migration_image_repository?: string | null;
  migration_image_tag?: string | null;
  // Exposition publique (Ingress)
  expose_public?: boolean;
  // nginx.conf custom (chart nubi-code-app >= 0.5.0). '' = reset au défaut chart.
  nginx_custom_config?: string | null;
  // PostgreSQL uniquement : bases additionnelles (chart nubi-postgres >= 1.2.0)
  additional_databases?: Array<{
    name: string;
    user?: string;
    password?: string;
    owner?: string;
  }>;
}

export interface AdditionalDatabaseMasked {
  name: string;
  user?: string;
  owner?: string;
  has_password: boolean;
}

export interface BuiltImage {
  build_id: string;
  task_id: string;
  application_name: string;
  image_url: string;
  image_tag: string;
  status: string;
  trigger_type: string;
  project: {
    id: string;
    name: string;
    description: string;
  } | null;
  repository: {
    url: string;
    branch: string;
    commit_sha: string | null;
  };
  build_info: {
    created_at: string;
    started_at: string | null;
    completed_at: string | null;
    duration_seconds: number | null;
    dockerfile_path: string;
  };
  is_standalone: boolean;
  error_message: string | null;
}

export interface BuiltImagesResponse {
  builds: BuiltImage[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
  statistics: {
    total_builds: number;
    successful_builds: number;
    failed_builds: number;
    running_builds: number;
    success_rate: number;
  };
  filters_applied: {
    status: string | null;
    project_id: string | null;
  };
}

export interface RepositoryAccessRequest {
  repository_url: string;
  git_token_id: string;
}

export interface CodeApplicationCreate {
  nom: string;
  cpu: number;  // Will be aliased to cpu_requis by backend
  ram: number;  // Will be aliased to ram_requise by backend
  replicas?: number;
  certificat_tls_id?: string | null;
  nom_domaine?: string | null;
  helm_chart_version?: string;
  type_application: string;
  framework: string;  // Framework ID: 'python-django', 'react', 'nextjs', etc.
  repository_access?: RepositoryAccessRequest | null;
  repo_url?: string | null;  // Changed from url_repository to repo_url
  git_branch?: string;
  build_args?: Record<string, any> | null;
  image_registry_url?: string | null;  // Changed from url_image to image_registry_url
  image_tag?: string | null;
  // Environment variables
  environment_variables?: Record<string, string>;
  sensitive_variables?: Record<string, string>;
  expose_public?: boolean;
  // Port de l'application (défaut selon framework)
  port?: number;
  // Type de charge : web | private | worker | cron (worker = Deployment seul, cron = CronJob)
  workload_type?: string;
  // Expression cron (workload_type=cron)
  schedule?: string;
  // Commande de démarrage, en forme exec (liste) : le processus est PID 1, donc il reçoit
  // SIGTERM. Obligatoire pour un worker ou un cron, sinon l'image lance son serveur web.
  command?: string[] | null;
  // Health check path
  health_check_path?: string;
  // Migration job
  migration_enabled?: boolean;
  migration_command?: string[] | null;
  migration_block_on_failure?: boolean;
  migration_image_repository?: string | null;
  migration_image_tag?: string | null;
  // nginx.conf custom (chart nubi-code-app >= 0.5.0)
  nginx_custom_config?: string | null;
}

export interface ERPApplicationCreate {
  nom: string;
  cpu: number;  // Will be aliased to cpu_requis by backend
  ram: number;  // Will be aliased to ram_requise by backend
  replicas?: number;
  certificat_tls_id?: string | null;
  nom_domaine?: string | null;
  helm_chart_version?: string;
  type_application: string;
  image_registry_url: string;  // Changed from url_image to image_registry_url (required for ERP)
  image_tag?: string;
  helm_values?: Record<string, any> | null;
  database_config?: Record<string, any> | null;
}

export interface BuildpackTemplate {
  id: string;
  name: string;
  language: string;
  type: string;
  description: string;
  dockerfile_template: string;
}

export interface BuildRecord {
  id: string;
  app_name: string;
  status: 'PENDING' | 'BUILDING' | 'SUCCESS' | 'FAILED';
  build_logs?: string;
  created_at: string;
  completed_at?: string;
}

export interface AppMetrics {
  cpu_usage: number;
  memory_usage: number;
  storage_usage: number;
  bandwidth_usage: number;
}

export interface ApiError {
  detail: string | Array<{
    type: string;
    loc: Array<string | number>;
    msg: string;
    input: unknown;
  }>;
}

// Types pour les membres de projet
export interface ProjectMember {
  id: string;
  projet_id: string;
  utilisateur_id: string;
  role_projet: 'admin' | 'developer' | 'user';
  nom: string;
  prenom: string;
  email: string;
}

export interface ProjectMemberCreate {
  email_utilisateur_invite: string;
  role_projet: 'admin' | 'developer' | 'user';
}

export interface ProjectMemberUpdate {
  role_projet: 'admin' | 'developer' | 'user';
}

// Types pour les invitations
export interface Invitation {
  id: string;
  projet_id: string;
  email_invite: string;
  role_projet: string;
  statut: string;
  date_creation: string;
  date_expiration: string;
  date_acceptation?: string | null;
}

export interface InvitationAcceptRequest {
  token_invitation: string;
  mot_de_passe?: string | null;
  prenom?: string | null;
  nom?: string | null;
  // Obligatoire UNIQUEMENT pour un nouvel utilisateur : accepter une invitation sans
  // compte en cree un, donc l'API exige l'acceptation la (422). Un membre deja inscrit
  // ne recree rien et n'a rien a renvoyer.
  conditions_version?: string | null;
}

export interface InvitationAcceptResponse {
  message: string;
  projet_id: string;
  utilisateur_id: string;
  access_token?: string | null;
}

export interface InvitationInfo {
  invitation_id: string;
  email: string;
  role: string;
  project_name: string;
  project_id: string;
  expires_at: string;
  user_exists: boolean;
}

// Response pour l'ajout d'un membre
export interface AddMemberResponse {
  message: string;
  type: 'member_added' | 'invitation_sent';
  member?: ProjectMember;
  invitation_id?: string;
  invitation_token?: string;
  invitation_url?: string;
  expires_at?: string;
}

// Type pour les membres invitables (Feature 2: Two-Way Invitations)
export interface InvitableMember {
  utilisateur_id: string;
  nom: string;
  prenom: string;
  email: string;
  role_organisation: 'owner' | 'admin' | 'member';
}

// ============================================================================
// PRICING TYPES
// ============================================================================

export interface PricingCalculationRequest {
  cpu: number;
  ram: number;
  storage: number;
}

export interface ResourceCostBreakdown {
  amount: number;
  unit_price: number;
  cost: number;
}

export interface PricingBreakdown {
  resources: {
    cpu: ResourceCostBreakdown;
    ram: ResourceCostBreakdown;
    storage: ResourceCostBreakdown;
  };
  platform_fee: number;
  subtotal: number;
  total: number;
  monthly: number;
  daily: number;
  hourly: number;
}

export interface CompetitorComparison {
  competitor: string;
  their_price: number;
  our_price: number;
  monthly_saving: number;
  yearly_saving: number;
  note: string;
}

export interface PricingComparisonResponse {
  competitors: Record<string, any>;
  savings: CompetitorComparison[];
  value_proposition: string;
}

export interface PricingCalculationResponse {
  breakdown: PricingBreakdown;
  comparison?: PricingComparisonResponse;
}

export interface ResourceConfig {
  cpu: number;
  ram: number;
  storage: number;
}

export interface PricingDetails {
  monthly_price: number;
  daily_price: number;
  hourly_price: number;
}

export interface PresetResponse {
  id: string;
  name: string;
  size: 'small' | 'medium' | 'large';
  description: string;
  usage_description: string;
  resources: ResourceConfig;
  pricing: PricingDetails;
}

export interface ApplicationPresetsResponse {
  application_type: 'wordpress' | 'odoo';
  presets: PresetResponse[];
  custom_available: boolean;
}

export interface AllApplicationPresetsResponse {
  applications: Record<string, ApplicationPresetsResponse>;
}

export interface PresetRecommendationRequest {
  application_type: 'wordpress' | 'odoo';
  expected_users?: number;
  traffic_level?: 'low' | 'medium' | 'high' | 'very_high';
}

export interface RecommendedPresetResponse {
  recommended: boolean;
  preset_id: string;
  name: string;
  size: 'small' | 'medium' | 'large';
  description: string;
  usage_description: string;
  resources: ResourceConfig;
  monthly_price: number;
  reasoning: string;
}

export interface ProjectCost {
  project_id: string;
  project_name: string;
  usage: {
    cpu_used: number;
    ram_used: number;
    storage_used: number;
  };
  monthly_cost: number;
}

export interface UserPricingUsageResponse {
  user_id: string;
  total_monthly_cost: number;
  total_yearly_cost: number;
  projects: ProjectCost[];
  currency: string;
}

export interface ProjectEstimationResponse {
  estimation: PricingBreakdown;
  recommendations: Array<{
    type: string;
    message: string;
    preset?: PresetResponse;
  }>;
  app_type?: string;
}

// ============================================================================
// OAUTH & REPOSITORY CONNECTION TYPES
// ============================================================================

export interface OAuthProvider {
  id: number;
  name: string;
  display_name: string;
  // Pour une GitHub App, cette URL est celle de l'INSTALLATION
  // (https://github.com/apps/<slug>/installations/new) : une App ne s'autorise pas,
  // elle s'installe.
  authorize_url: string;
  scopes: string;
  is_enabled: boolean;
  // OPTIONNELS, et il faut savoir pourquoi : `GET /oauth-v2/providers` les rend
  // toujours, mais l'adaptateur de `/git/repositories/connected` FABRIQUE un
  // `oauth_provider` de toutes pieces pour satisfaire ce type (`api.ts`,
  // `getConnectedRepositories`). La console doit donc exiger `prete === true` et
  // jamais `prete !== false` : absent veut dire « on ne sait pas », ce qui ne doit
  // pas faire afficher un bouton.
  auth_kind?: string;
  app_id?: string | null;
  // Des BOOLEENS, jamais les valeurs : l'API ne fait jamais sortir une cle privee
  // ni un secret de webhook.
  private_key_posee?: boolean;
  webhook_secret_pose?: boolean;
  // Calcule par l'API. La console ne reimplemente PAS « ce fournisseur est-il
  // utilisable » : deux regles, c'est un bouton affiche qui mene a un 503.
  prete?: boolean;
}

export interface ConnectedRepository {
  id: number;
  user_id: string;
  oauth_provider_id: number;
  project_id: string | null;
  provider_repo_id: string;
  repo_name: string;
  repo_owner: string;
  repo_url: string;
  clone_url: string;
  default_branch: string;
  monitored_branch: string;
  webhook_enabled: boolean;
  webhook_id: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  oauth_provider: OAuthProvider;
  repo_metadata?: {
    private: boolean;
    full_name: string;
  };
}

export interface QuickConnectRequest {
  repo_url: string;
  project_id?: string;
  monitored_branch?: string;
}

export interface QuickConnectResponse {
  success: boolean;
  message: string;
  auth_url: string;
  state: string;
  repository: {
    provider: string;
    owner: string;
    name: string;
    url: string;
  };
  next_steps: string[];
}

// ============================================================================
// INTELLIGENT BUILD TYPES
// ============================================================================

// Old build interface (deprecated - for compatibility)
export interface BuildRequest {
  repository_url: string;
  git_provider: 'github' | 'gitlab';
  git_token: string;
  git_username?: string;
  image_tag?: string;
  app_type?: string;
  branch?: string;
}

export interface BuildResponse {
  success: boolean;
  build_id: string;
  message: string;
  image_url?: string;
  image_tag?: string;
  registry?: string;
  build_logs_url?: string;
}

// New intelligent build interfaces
export interface IntelligentBuildRequest {
  git_url: string;
  app_name: string;
  environment_variables?: Record<string, string>;
  build_strategy?: 'auto' | 'simple_buildpack' | 'enhanced_buildpack' | 'smart_dockerfile' | 'docker_compose';
  auto_deploy?: boolean;
  commit_sha?: string;
}

export interface IntelligentBuildResponse {
  success: boolean;
  build_id: string;
  message: string;
  analysis?: {
    detected_language?: string;
    detected_framework?: string;
    confidence?: number;
    recommendations?: string[];
  };
  build_strategy?: {
    strategy: string;
    reason?: string;
  };
  environment_analysis?: {
    detected_env_files?: string[];
    suggested_variables?: string[];
  };
  monitoring?: {
    build_url?: string;
    logs_url?: string;
  };
}

export interface TaskStatus {
  name: string;
  status: 'pending' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  start_time?: string | null;
  completion_time?: string | null;
}

export interface BuildStatus {
  build_id: string;
  status: 'pending' | 'running' | 'success' | 'succeeded' | 'failed' | 'cancelled';
  image_url?: string;
  image_tag?: string;
  created_at: string;
  // Support both field names from different API endpoints
  started_at?: string;
  start_time?: string;
  completed_at?: string;
  completion_time?: string;
  duration_seconds?: number;
  error_message?: string;
  // Pipeline run ID for Tekton builds
  pipeline_run_id?: string;
  stages?: Array<{
    name: string;
    status: string;
    started_at?: string;
    completed_at?: string;
  }>;
  tasks?: TaskStatus[];
  /**
   * L'API a-t-elle une analyse de vulnerabilite pour ce build ?
   * Faux sur un build buildpack (kpack) : le scan vit dans une tache de PipelineRun
   * Tekton. Avant ce champ, l'ecran omettait l'onglet sans jamais dire pourquoi.
   */
  scan_available?: boolean;
}

export interface BuildLogs {
  build_id: string;
  logs: Record<string, string[]>; // Dictionnaire de tâches -> liste de lignes de logs
  pipeline_run_id?: string;
  task_filter?: string;
}

// ============================================================================
// IMAGE IMPORT TYPES (NubiImport)
// ============================================================================

export interface ImageImportRequest {
  source_image: string;
  target_name: string;
  target_tag: string;
  credentials?: {
    registry_url: string;
    username: string;
    password: string;
  };
  credential_id?: string;
  project_id?: string;
}

export interface ImageImportResponse {
  success: boolean;
  import_id: string;
  message: string;
  source_image?: string;
  target_image?: string;
  status?: string;
  monitoring?: {
    status_url?: string;
    logs_url?: string;
    dashboard_url?: string;
  };
}

export interface ImportStatus {
  import_id: string;
  status: 'pending' | 'importing' | 'succeeded' | 'failed' | 'cancelled';
  source_image?: string;
  target_image?: string;
  image_digest?: string;
  import_size_bytes?: number;
  pipeline_run_id?: string;
  start_time?: string;
  completion_time?: string;
  error_message?: string;
}

export interface ImportLogs {
  import_id: string;
  pipeline_run_id?: string;
  logs: Record<string, string[]>;
}

export interface ValidateImageRequest {
  source_image: string;
  credentials?: {
    registry_url: string;
    username: string;
    password: string;
  };
  credential_id?: string;
  project_id?: string;
}

export interface ValidateImageResponse {
  exists: boolean;
  registry: string;
  repository: string;
  tag: string;
  size_bytes?: number;
  message: string;
  needs_auth?: boolean;
}

export interface StoreCredentialsRequest {
  registry_url: string;
  username: string;
  password: string;
  projet_id?: string;
}

export interface StoreCredentialsResponse {
  valid: boolean;
  credential_id?: string;
  registry_url: string;
  message: string;
}

export interface RegistryCredentialItem {
  id: string;
  registry_url: string;
  username: string;
  is_valid?: boolean;
  last_validated_at?: string;
  validation_error?: string;
  projet_id?: string;
  created_at?: string;
}

export interface CredentialListResponse {
  credentials: RegistryCredentialItem[];
  total: number;
}

// ============================================================================
// ODOO MODULES TYPES
// ============================================================================

export interface OdooModuleAdd {
  deployment_id: string;
  module_type: 'git' | 'zip';
  // Option 1: Utiliser un repository connecté (RECOMMANDÉ - supporte repos privés)
  connected_repo_id?: number;
  git_subpath?: string;
  // Option 2: URL publique directe (uniquement repos publics)
  git_url?: string;
  git_branch?: string;
  module_name: string;
  auto_install?: boolean;
}

export interface OdooModule {
  id: number;
  deployment_id: string;
  module_name: string;
  module_type: 'git' | 'zip';
  git_url?: string | null;
  git_branch?: string | null;
  is_installed: boolean;
  created_at: string;
  updated_at?: string | null;
}

export interface OdooModuleListResponse {
  modules: OdooModule[];
  count: number;
}

export interface OdooModuleLogEntry {
  module_id: number;
  module_name: string;
  module_type: string;
  container_name: string;
  logs: string | null;
  status: 'ok' | 'no_logs' | 'error' | 'unknown';
  error?: string;
}

export interface OdooModuleLogsResponse {
  application: string;
  argocd_app?: string;
  modules: OdooModuleLogEntry[];
  modules_count: number;
  since: string;
  tail_lines: number;
  timestamp: string;
  message?: string;
}

// ============================================================================
// BACKUP & RESTORE TYPES
// ============================================================================

export interface BackupConfiguration {
  id: string;
  deployment_id: string;
  schedule: string;
  retention_policy: string; // Format: "7d", "30d", etc.
  enabled: boolean;
  s3_path: string;
  created_at: string;
  updated_at: string;
}

export interface BackupConfigurationCreate {
  schedule: string;
  retention_policy: string; // Format: "7d", "30d", etc.
  enabled: boolean;
}

export interface Backup {
  id: string;
  deployment_id: string;
  backup_name: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  backup_type: 'scheduled' | 'manual';
  size_bytes: number | null;
  s3_path: string;
  started_at: string;
  stopped_at: string | null;
  completed_at: string | null;
  error_message: string | null;
}

export interface BackupListResponse {
  backups: Backup[];
  total: number;
  page: number;
  page_size: number;
}

export interface CreateOnDemandBackupResponse {
  backup_id: string;
  message: string;
  status: string;
}

export interface RestoreOperation {
  id: string;
  deployment_id: string;
  backup_id: string;
  status: 'pending' | 'in_progress' | 'awaiting_validation' | 'completed' | 'failed' | 'cancelled';
  target_cluster: string;
  backup_timestamp: string;
  validated_at: string | null;
  validated_by: string | null;
  started_at: string | null;
  created_at: string;
  completed_at: string | null;
  updated_at: string;
  error_message: string | null;
}

export interface RestoreListResponse {
  restore_operations: RestoreOperation[];
  total: number;
}

export interface RestoreValidateRequest {
  confirm: boolean;
}

// ============================================================================
// REGION TYPES (Multi-cluster deployment)
// ============================================================================

export interface Region {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  organisation_id?: string | null;
  is_default: boolean;
  active: boolean;
  type: 'global' | 'custom';
  is_configured: boolean;
  region_source: 'manual' | 'nubicloud' | 'custom_install';
  connection_status: 'pending' | 'provisioning' | 'connected' | 'disconnected' | 'error';
  headscale_ip?: string | null;
  last_seen_at?: string | null;
  connection_error?: string | null;
  created_at: string;
  updated_at?: string | null;
}

export interface GenerateInstallScriptResponse {
  download_url: string;
  curl_command: string;
  expires_at: string;
  script_valid_until: string;
}

export interface RegionManualCallbackRequest {
  callback_secret: string;
  tailscale_ip: string;
  bearer_token: string;
  ca_data?: string;
  api_port: number;
}

export interface RegionConnectionInfo {
  connection_status: string;
  headscale_ip?: string | null;
  headscale_node_id?: string | null;
  last_seen_at?: string | null;
  connection_error?: string | null;
  cluster_url?: string | null;
}

export interface RegionInfo {
  id: string;
  name: string;
  slug: string;
}

export interface RegionCreate {
  name: string;
  description?: string | null;
  region_source?: 'manual' | 'nubicloud' | 'custom_install';
}

export interface RegionUpdate {
  name?: string;
  description?: string | null;
  active?: boolean;
}

// ============================================================================
// ORGANISATION LAYER TYPES
// ============================================================================

// Organisation Types
// Plans publics actuels (cf. grille nubifront + SUBSCRIPTION_PLANS backend).
// STARTER/SCALE retirés (remappés STARTER→PROFESSIONAL, SCALE→BUSINESS).
export type PlanTypeKey = 'FREE' | 'PROFESSIONAL' | 'BUSINESS' | 'ENTERPRISE';
export type SelectablePlanKey = 'FREE' | 'PROFESSIONAL' | 'BUSINESS';
export type PlanRegion = 'afrique' | 'europe';
// Prix mensuel FCFA différencié par région, ou null pour ENTERPRISE (sur devis).
export type PlanPrice = { afrique: number; europe: number } | null;

export interface Organisation {
  id: string;
  nom: string;
  plan_type: PlanTypeKey;
  quota_global_cpu: number;
  quota_global_ram: number;
  quota_global_stockage: number;
  nom_dossier_git?: string;
  date_creation: string;
  is_deleted: boolean;
}

export interface OrganisationCreate {
  nom: string;
  plan_type?: SelectablePlanKey;
}

export interface OrganisationUpdate {
  nom?: string;
  plan_type?: string;
  quota_global_cpu?: number;
  quota_global_ram?: number;
  quota_global_stockage?: number;
}

export interface OrganisationDeleteRequest {
  confirmation_text: string;
  deletion_reason?: string;
  understand_consequences: boolean;
}

export interface OrganisationRestoreResponse {
  message: string;
  organisation: Organisation;
}

// Organisation Member Types
export interface OrganisationMember {
  id: string;
  organisation_id: string;
  utilisateur_id: string;
  role_organisation: 'owner' | 'admin' | 'member';
  date_ajout: string;
  auto_added: boolean;
  // User info
  nom: string;
  prenom: string;
  email: string;
}

export interface OrganisationMemberCreate {
  email_utilisateur_invite: string;
  role_organisation: 'admin' | 'member';
}

export interface OrganisationMemberUpdate {
  role_organisation: 'admin' | 'member';
}

export interface InviteMemberResponse {
  message: string;
  invitation_id: string;
  email: string;
  role: string;
  expires_at: string;
}

// Organisation Invitation Types
export interface OrganisationInvitation {
  id: string;
  organisation_id: string;
  email_invite: string;
  role_organisation: string;
  statut: 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'REVOKED';
  date_creation: string;
  date_expiration: string;
  date_acceptation?: string | null;
}

export interface OrganisationInvitationInfo {
  id: string;
  organisation_id: string;
  organisation_nom: string;
  email_invite: string;
  role_organisation: string;
  statut: string;
  date_expiration: string;
  inviteur_nom: string;
  inviteur_prenom: string;
  user_exists: boolean;
}

export interface OrganisationInvitationAcceptRequest {
  // Obligatoire UNIQUEMENT pour un nouvel utilisateur : accepter une invitation sans
  // compte en cree un, donc l'API exige l'acceptation la (422).
  conditions_version?: string | null;
  token_invitation: string;
  // For new users
  mot_de_passe?: string | null;
  prenom?: string | null;
  nom?: string | null;
}

export interface OrganisationInvitationAcceptResponse {
  message: string;
  organisation_id: string;
  utilisateur_id: string;
  role_organisation: string;
  access_token?: string | null; // If new user created
  verification_required?: boolean; // If account needs verification
  verification_sent?: boolean; // If verification email was sent
}

// Account Verification Types
export interface VerifyAccountRequest {
  email: string;
  token: string; // 6-digit verification code
}

export interface VerifyAccountResponse {
  message: string;
  user_id: string;
}

export interface ResendVerificationRequest {
  email: string;
}

export interface ResendVerificationResponse {
  message: string;
  email: string;
  expires_in_hours?: number;
}

// Quota Types
export interface QuotaUsage {
  used: number;
  total: number;
  available: number;
  percentage: number;
}

export interface OrganisationQuotaUsage {
  organisation_id: string;
  organisation_nom: string;
  cpu: QuotaUsage;
  ram: QuotaUsage;
  storage: QuotaUsage;
  project_count: number;
}

// Subscription Plan Types
export interface SubscriptionPlanFeatures {
  quota_cpu: number;
  quota_ram: number;
  quota_storage: number;
  max_projects: number; // -1 for unlimited
  max_members: number; // -1 for unlimited
  price_monthly: PlanPrice; // { afrique, europe } en FCFA, ou null (ENTERPRISE = sur devis)
  features: string[];
  selectable: boolean;
}

export interface SubscriptionPlan {
  name: string;
  description: string;
  quota_cpu: number;
  quota_ram: number;
  quota_storage: number;
  max_projects: number;
  max_members: number;
  max_s3_buckets?: number;
  price_monthly: PlanPrice; // { afrique, europe } en FCFA, ou null (ENTERPRISE = sur devis)
  features?: string[];
  selectable: boolean;
}

export interface AvailablePlansResponse {
  plans: Record<string, SubscriptionPlan>;
  note: string;
}

export interface PlanChangeRequest {
  new_plan_type: SelectablePlanKey;
}

export interface EnterpriseRequestData {
  name: string;
  email: string;
  phone?: string;
  company_size?: string;
  message?: string;
  // Le nom du type est historique : cet endpoint porte désormais AUSSI les
  // demandes de PROFESSIONAL et BUSINESS, les plans payants n'étant plus en
  // libre-service. Facultatif, défaut ENTERPRISE côté serveur.
  plan_type?: string;
}

export interface EnterpriseRequestResponse {
  message: string;
  // `pending` = la notification est partie. `pending_unconfirmed` = la demande
  // est enregistrée mais le mail n'a PAS pu être envoyé — à afficher comme un
  // avertissement, pas comme un succès.
  status: string;
  organisation: string;
  contact: string;
  plan?: string;
}

// ============================================
// Prometheus Metrics Types (aligned with backend schemas)
// ============================================

/** Série temporelle Prometheus : labels + [[timestamp, "value"], ...] */
export interface MetricSeries {
  metric: Record<string, string>;
  values: [number, string][];
}

/** Métrique instantanée Prometheus : labels + [timestamp, "value"] */
export interface InstantMetric {
  metric: Record<string, string>;
  value: [number, string];
}

export interface PrometheusHealthResponse {
  connected: boolean;
  status: string;
  error?: string;
}

export interface CPUMetricsResponse {
  usage_by_pod: InstantMetric[];
  requests_by_pod: InstantMetric[];
  limits_by_pod: InstantMetric[];
  quota_requests: InstantMetric[];
  quota_limits: InstantMetric[];
  utilization_requests: InstantMetric[];
  utilization_limits: InstantMetric[];
  timeseries?: Record<string, MetricSeries[]>;
}

export interface MemoryMetricsResponse {
  usage_by_pod: InstantMetric[];
  requests_by_pod: InstantMetric[];
  limits_by_pod: InstantMetric[];
  rss_by_pod: InstantMetric[];
  cache_by_pod: InstantMetric[];
  quota_requests: InstantMetric[];
  quota_limits: InstantMetric[];
  utilization_requests: InstantMetric[];
  utilization_limits: InstantMetric[];
  timeseries?: Record<string, MetricSeries[]>;
}

export interface NetworkMetricsResponse {
  receive_bandwidth: InstantMetric[];
  transmit_bandwidth: InstantMetric[];
  receive_packets: InstantMetric[];
  transmit_packets: InstantMetric[];
  receive_dropped: InstantMetric[];
  transmit_dropped: InstantMetric[];
  timeseries?: Record<string, MetricSeries[]>;
}

export interface StorageMetricsResponse {
  iops_total: InstantMetric[];
  throughput_total: InstantMetric[];
  reads_iops: InstantMetric[];
  writes_iops: InstantMetric[];
  reads_throughput: InstantMetric[];
  writes_throughput: InstantMetric[];
  timeseries?: Record<string, MetricSeries[]>;
}

export interface AllMetricsResponse {
  namespace: string;
  cluster: string;
  time_range: Record<string, unknown>;
  cpu: CPUMetricsResponse;
  memory: MemoryMetricsResponse;
  network: NetworkMetricsResponse;
  storage: StorageMetricsResponse;
}

// =====================================================
// Asset Transfer Types (Organisation V2)
// =====================================================

export interface AssetPreview {
  projects: Array<{ id: string; nom: string; nom_dossier_git?: string }>;
  project_count: number;
  environment_count: number;
  application_count: number;
  message: string;
}

export interface AssetTransferResult {
  projects_transferred: number;
  environments_transferred: number;
  applications_transferred: number;
  gitlab_folders_moved: number;
  errors: string[];
}

export interface RemoveMemberPreviewResponse {
  action: 'preview';
  warning: boolean;
  message: string;
  assets_preview: AssetPreview;
  requires_confirmation: boolean;
}

export interface RemoveMemberSuccessResponse {
  message: string;
  details: {
    action: 'completed';
    success: boolean;
    message: string;
    projects_removed: number;
    transfer_result: AssetTransferResult | null;
  };
}

export type RemoveMemberResponse = RemoveMemberPreviewResponse | RemoveMemberSuccessResponse;

export interface DeleteAccountPreviewResponse {
  action: 'preview';
  warning: boolean;
  message: string;
  assets_preview: AssetPreview;
  requires_confirmation: boolean;
}

export interface DeleteAccountSuccessResponse {
  message: string;
  details: {
    action: 'completed';
    success: boolean;
    message: string;
    transfer_result: AssetTransferResult | null;
  };
}

export type DeleteAccountResponse = DeleteAccountPreviewResponse | DeleteAccountSuccessResponse;

// ============================================
// Migration & Job Logs Types
// ============================================

export interface JobInfo {
  name: string;
  status: string;
  health: string;
  namespace: string;
  uid: string;
  creation_timestamp: string;
  sync_wave: number;
}

export interface ApplicationJobsResponse {
  application: string;
  namespace: string;
  argocd_app: string;
  jobs: JobInfo[];
  jobs_count: number;
  timestamp: string;
}

export interface PodInfo {
  name: string;
}

export interface JobLogsResponse {
  job_name: string;
  application: string;
  namespace: string;
  argocd_app?: string;
  logs: string;
  pods: PodInfo[];
  lines_count: number;
  status: string;
  since: string;
  tail_lines: number;
  timestamp: string;
}

export interface MigrationLogsResponse {
  application: string;
  namespace: string;
  argocd_app?: string;
  migration_job_name: string;
  migration_enabled: boolean;
  job_status?: string;
  message?: string;
  logs: string;
  pods?: PodInfo[];
  lines_count?: number;
  all_jobs?: string[];
  since?: string;
  tail_lines?: number;
  timestamp: string;
}

// ========================================
// Build Configuration Types (Webhook Auto-Build)
// ========================================

export type BuildStrategy = 'dockerfile' | 'buildpack';

export interface BuildConfiguration {
  id: number;
  connected_repository_id: number;
  name: string;
  is_default: boolean;
  build_strategy: BuildStrategy;
  source_directory: string;
  dockerfile_path: string;
  builder_image: string | null;
  image_name_override: string | null;
  image_tag_pattern: string;
  build_env_vars: Record<string, string>;
  auto_build_enabled: boolean;
  trigger_on_push: boolean;
  trigger_on_tag: boolean;
  trigger_on_pr: boolean;
  branch_filter: string | null;
  path_filter: string | null;
  auto_deploy_enabled: boolean;
  deploy_environment_id: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface BuildConfigurationCreate {
  name: string;
  is_default?: boolean;
  build_strategy?: BuildStrategy;
  source_directory?: string;
  dockerfile_path?: string;
  builder_image?: string | null;
  image_name_override?: string | null;
  image_tag_pattern?: string;
  build_env_vars?: Record<string, string>;
  auto_build_enabled?: boolean;
  trigger_on_push?: boolean;
  trigger_on_tag?: boolean;
  trigger_on_pr?: boolean;
  branch_filter?: string | null;
  path_filter?: string | null;
  auto_deploy_enabled?: boolean;
  deploy_environment_id?: string | null;
}

export interface BuildConfigurationUpdate {
  name?: string;
  is_default?: boolean;
  build_strategy?: BuildStrategy;
  source_directory?: string;
  dockerfile_path?: string;
  builder_image?: string | null;
  image_name_override?: string | null;
  image_tag_pattern?: string;
  build_env_vars?: Record<string, string>;
  auto_build_enabled?: boolean;
  trigger_on_push?: boolean;
  trigger_on_tag?: boolean;
  trigger_on_pr?: boolean;
  branch_filter?: string | null;
  path_filter?: string | null;
  auto_deploy_enabled?: boolean;
  deploy_environment_id?: string | null;
}

export interface BuildConfigurationListResponse {
  configs: BuildConfiguration[];
  total: number;
  repository_id: number;
  repository_name: string;
}

export interface BuildTriggerRequest {
  branch?: string;
  commit_sha?: string;
  image_tag?: string;
}

export interface BuildTriggerResponse {
  success: boolean;
  build_id: string;
  pipeline_run_name: string;
  config_name: string;
  message: string;
}

// ============================================
// Available Updates (Go Live)
// ============================================

export interface AvailableUpdate {
  build_id: string;
  image_tag: string;
  image_url: string;
  commit_sha: string;
  completed_at: string;
  app_name: string;
}

export interface AvailableUpdatesResponse {
  current_tag: string | null;
  available_updates: AvailableUpdate[];
  has_update: boolean;
}

// ============================================
// SQL Editor
// ============================================

export interface SqlExecuteResponse {
  job_id: string;
  status: string;
}

export interface SqlResultResponse {
  job_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  columns: string[] | null;
  rows: any[][] | null;
  row_count: number | null;
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
}

// Studio session (per-user-per-deployment ephemeral BFF pod)
export interface StudioSessionResponse {
  session_id: string;
  pod_name: string;
  status: string; // K8s pod phase
  namespace: string;
  database: string; // DB effectivement ciblée par le pod BFF
}

// ============================================
// Console Kafka
// ============================================

export interface KafkaSessionResponse {
  session_id: string;
  pod_name: string;
  bootstrap: string; // adresse interne, utilisable depuis une app du même environnement
  /**
   * Identité SASL = nom de la ressource KafkaUser. Ce n'est PAS la valeur saisie
   * à la création du service : Strimzi publie username="<nom du KafkaUser>" dans
   * le sasl.jaas.config. L'afficher telle quelle évite au client de tenter de
   * s'authentifier avec le mauvais identifiant.
   */
  username: string;
  reused: boolean;
}

export interface KafkaTopic {
  name: string;
  partitions: number;
  replicationFactor: number | null;
}

export interface KafkaGroup {
  groupId: string;
  state: string | null;
}

export interface KafkaGroupLagPartition {
  topic: string;
  partition: number;
  committed: number;
  logEndOffset: number | null;
  lag: number | null;
}

export interface KafkaMessage {
  offset: number;
  timestamp: number | null;
  key: string | null;
  value: string | null;
}

// ============================================
// Audit Logs
// ============================================

export interface AuditLogEntry {
  id: number;
  user_id: string;
  user_email: string | null;
  user_name: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  organisation_id: string | null;
  project_id: string | null;
  ip_address: string | null;
  success: boolean;
  error_message: string | null;
  details: Record<string, any> | null;
  created_at: string;
}

export interface AuditLogListResponse {
  logs: AuditLogEntry[];
  total: number;
  page: number;
  page_size: number;
}

// =====================================================
// NubiS3 — S3-as-a-service via Backblaze B2
// =====================================================

export interface S3Bucket {
  id: string;
  organisation_id: string;
  name: string;
  description: string | null;
  b2_bucket_name: string;
  region: string;
  endpoint: string;
  quota_bytes: number | null;
  used_bytes: number;
  object_count: number;
  created_at: string;
}

export interface S3BucketCreate {
  organisation_id: string;
  name: string;
  description?: string;
  quota_bytes?: number | null;
}

export interface S3AccessKey {
  id: string;
  s3_bucket_id: string;
  name: string;
  b2_key_id: string;
  capabilities: string[];
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export interface S3AccessKeyCreate {
  name: string;
  capabilities: string[];
}

/** Réponse de POST /access-keys — contient le secret affiché UNE SEULE FOIS. */
export interface S3AccessKeySecret extends S3AccessKey {
  application_key: string;
  endpoint: string;
  bucket_name: string;
  region: string;
}

// Objects (files in a bucket)

export interface S3Object {
  key: string;
  size: number;
  content_type: string | null;
  uploaded_at: string | null;
}

export interface S3ObjectListResponse {
  objects: S3Object[];
  next_cursor: string | null;
}

export interface S3PresignedUploadRequest {
  key: string;
  content_type?: string;
}

export interface S3PresignedUrlResponse {
  url: string;
  expires_in: number;
}

/** Sets prédéfinis exposés au frontend (côté backend whitelist). */
export const S3_CAPABILITY_PRESETS: Record<'read-only' | 'read-write' | 'full', string[]> = {
  'read-only': ['listFiles', 'readFiles', 'listBuckets', 'readBuckets'],
  'read-write': ['listFiles', 'readFiles', 'writeFiles', 'deleteFiles', 'listBuckets', 'readBuckets'],
  'full': [
    'listFiles', 'readFiles', 'shareFiles', 'writeFiles', 'deleteFiles',
    'listBuckets', 'readBuckets',
    'readBucketEncryption', 'readBucketRetentions',
    'readBucketNotifications', 'readBucketLogging',
    'readFileLegalHolds', 'readFileRetentions',
  ],
};

export interface AuditLogFilters {
  project_id?: string;
  action?: string;
  resource_type?: string;
  success?: boolean;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}

// ============================================================================
// BLUEPRINT / STACK types (nubicloud.yaml multi-services)
// ============================================================================

export interface EnvBinding {
  key: string;
  source: string;
  sensitive: boolean;
}

export interface BrickPlan {
  name: string;
  kind: string;
  action: string;
  service_type?: string | null;
  exposed: boolean;
  domain?: string | null;
  port?: number | null;
  replicas?: number | null;
  cpu?: number | null;
  memory?: number | null;
  storage?: number | null;
  depends_on: string[];
  env_bindings: EnvBinding[];
  warnings: string[];
}

export interface StackPlan {
  name?: string | null;
  order: string[];
  bricks: BrickPlan[];
  total_cpu: number;
  total_memory: number;
  total_storage: number;
  quota_ok?: boolean | null;
  prompts: string[];
  warnings: string[];
}

export interface BrickStatus {
  id: string;
  name: string;
  kind: string;
  state: string;
  depends_on: string[];
  deployment_id?: string | null;
  build_configuration_id?: number | null;
  last_error?: string | null;
}

export interface StackSummary {
  id: string;
  name?: string | null;
  environment_id: string;
  manifest_source: string;
  sync_status: string;
  manifest_yaml?: string | null;   // manifeste sérialisé → édition d'un stack existant
  bricks: BrickStatus[];
}

export interface DetectRequest {
  connected_repository_id?: number;
  repo_url?: string;
  name?: string;
}

export interface DetectResponse {
  manifest: any;
  manifest_yaml: string;
  source: 'compose' | 'detection';
  notes: string[];
}

// Config files : fichiers de configuration custom montes dans le container.
// Cf. docs/config-files-feature-design.md (backend v2.15.1+, chart nubi-code-app >= 0.10.0).
export interface ConfigFile {
  id: string;
  deployment_id: string;
  path: string;
  content: string;
  sensitive: boolean;
  size_bytes: number;
  created_at: string;
  updated_at?: string | null;
}

export interface ConfigFileInput {
  path: string;
  content: string;
  sensitive: boolean;
}

// ─── NubieAI : generateur de sites vitrines ───
// Les jetons d'une direction partent au client pour qu'il affiche un apercu de la
// palette sans rendre la page : c'est ce qui rend l'ecran des styles instantane.

export interface NubieAiDirection {
  slug: string;
  nom: string;
  raison: string;
  /* LE CHAMP `motif` A DISPARU DE L'API le 2026-10-31. La direction en portait un --
   * `artisan` prescrivait des filets verticaux tous les 2,5 rem -- que CET ECRAN ne
   * montrait JAMAIS au moment du choix, et qu'une garde exigeait ensuite de poser sur
   * la page. Le client choisissait une palette et heritait d'une trame sans l'avoir
   * vue ni demandee. La profondeur appartient desormais au socle (`nubie-fond-*`). */
  strategie_sans_photo: string;
  jetons: Record<string, string>;
}

export interface NubieAiContact {
  telephone?: string | null;
  whatsapp?: string | null;
  email?: string | null;
  adresse?: string | null;
}

export interface NubieAiSiteResume {
  id: string;
  nom: string;
  slug: string | null;
  direction: string | null;
  statut: 'brouillon' | 'publie' | 'en_sommeil';
  /**
   * Calculee par l'API, jamais reconstruite ici.
   *
   * Le domaine est un reglage de plateforme (`SITES_DOMAINE_PUBLIC`) : le recopier
   * dans le front en ferait une valeur a garder en phase avec la configuration, et un
   * jour l'une des deux aurait tort. `null` tant que le site n'a jamais ete publie.
   */
  url_publique: string | null;
  nb_fichiers: number;
  cree_le: string;
  modifie_le: string;
}

/**
 * Une photo televersee par le client.
 *
 * `url` est une adresse de l'API, AUTHENTIFIEE : l'original vit dans un bucket prive
 * sous un prefixe que le proxy ne sert pas, donc il n'existe aucune URL publique avant
 * publication. Elle ne peut donc PAS aller dans un `src` -- l'API etant sur une autre
 * origine, la balise ne porterait pas le cookie. Passer par `getNubieAiMediaBlob`.
 */
export interface NubieAiMedia {
  id: string;
  url: string | null;
  /** `data-nubie-slot` de l'emplacement vise. `null` = televersee, pas encore placee. */
  slot: string | null;
  alt: string;
  largeur: number;
  hauteur: number;
  octets: number;
}

export interface NubieAiSiteDetail extends NubieAiSiteResume {
  brief: string | null;
  contact: NubieAiContact;
  socle_version: string | null;
  fichiers: Record<string, string>;
  /**
   * Les emplacements declares par les pages, lus par l'API DANS le HTML.
   *
   * C'est le modele qui decide combien de cadres il pose et comment il les nomme : les
   * deviner ici proposerait de placer une photo dans un emplacement inexistant.
   */
  emplacements: string[];
  medias: NubieAiMedia[];
  /**
   * Le brouillon avec les photos DEJA POSEES, pour l'apercu.
   *
   * L'injection se fait a la PUBLICATION, jamais dans le brouillon : c'est ce qui
   * protege les photos d'une regeneration. Du coup `fichiers` porte des cadres
   * VIDES, et l'apercu ne montrait rien avant publication.
   *
   * Rendu par l'API et NON reconstruit ici : c'est la meme fonction d'injection que
   * la publication. Le `src` pointe la route d'apercu de l'API -- a substituer par
   * une URL d'objet, l'API etant sur une autre origine.
   *
   * `null` quand aucune photo n'est placee : employer `fichiers` dans ce cas.
   */
  apercu_html: string | null;
}

/**
 * L'etat REEL d'un site sur le web.
 *
 * POURQUOI CE TYPE EXISTE, signale par l'utilisateur apres avoir publie : « il faut
 * quelque chose cote console pour nous dire si le site est en ligne ou pas, apres
 * publication il repond pas de suite ». `statut: 'publie'` et `url_publique` sont
 * poses des que les objets sont televerses -- mais le certificat est demande PAR SITE
 * en HTTP-01 et met de trente a quatre-vingt-dix secondes.
 *
 * PENDANT CETTE FENETRE LE VISITEUR NE VOIT PAS UN 404 : il voit un AVERTISSEMENT DE
 * SECURITE du navigateur, nginx servant son certificat par defaut faute de secret
 * TLS. C'est la pire premiere impression possible sur un site qu'on vient de mettre
 * en ligne -- et l'ecran disait « publie ».
 *
 * `etat` vaut `en_ligne`, `certificat_en_cours`, `certificat_absent`, `brouillon` ou
 * `inconnu`. `inconnu` est le defaut en cas de panne de lecture cote API : ne pas
 * savoir se DIT, parce qu'annoncer « en ligne » sans l'avoir verifie enverrait le
 * commercant partager a ses clients une adresse qui les effraie.
 */
export interface NubieAiEtatEnLigne {
  etat: 'en_ligne' | 'certificat_en_cours' | 'certificat_absent' | 'brouillon' | 'inconnu';
  /** Ecrit pour l'ecran par l'API : a afficher tel quel, jamais a reformuler. */
  message: string;
  url?: string | null;
}

export interface NubieAiResultatPublication {
  slug: string;
  url: string;
  fichiers: number;
  octets: number;
  socle_version: string;
  publie_le: string;
}

export interface NubieAiSocle {
  version: string;
  base_css: string;
  motion_js: string;
}

/**
 * Un tour du fil de discussion d'un site vitrine (V063).
 *
 * LE FIL EST PERSISTE COTE SERVEUR, et ce type vient de la route de LECTURE --
 * `GET /nubie-ai/sites/{id}/messages`. Avant elle, l'ecran n'affichait que le dernier
 * resultat : un rechargement de page laissait un cadre vide, ce qui etait le reproche
 * d'origine (« le cadre de prompt ne garde pas le fil de discussion »).
 *
 * `cree_le` est FACULTATIF a dessein : un tour ajoute localement en attendant la
 * reponse du modele n'a pas encore de date du serveur, et une date inventee ici serait
 * un fait faux affiche comme un fait.
 */
export interface NubieAiTour {
  role: 'user' | 'assistant';
  contenu: string;
  cree_le?: string | null;
}

/**
 * Une question posee au client, a valider d'un clic.
 *
 * `propositions` SONT DES SUPPOSITIONS DU MODELE, pas des faits. Tant que le client n'en
 * a clique aucune, aucune n'a le droit d'atteindre la page -- l'ecran doit le dire. Un
 * prix ou un volume invente qu'un client valide par inertie devient un faux
 * renseignement publie sur son commerce, de la meme classe que le numero de telephone
 * qu'on n'invente jamais.
 *
 * `propositions` est VIDE sur le genre `texte`, par REFUS de l'API et non par omission :
 * un champ libre pre-rempli est exactement la valeur inventee qu'on s'interdit.
 *
 * `fourchette` ne porte que des INTERVALLES (« 150-300 F », « moins de 50 ») : proposer
 * un nombre exact ferait cliquer le plus proche, donc publier une approximation
 * presentee comme exacte.
 */
export interface NubieAiQuestion {
  id: string;
  texte: string;
  genre: 'choix_multiple' | 'choix_unique' | 'fourchette' | 'texte';
  propositions: string[];
  facultatif: boolean;
}

export interface NubieAiResultatMessage {
  texte: string;
  fichiers_touches: string[];
  /** NON VIDE veut dire « le modele attend des reponses, il n'a rien construit ».
   *  L'ecran montre alors le formulaire et NON un compte rendu : demander n'est pas
   *  echouer, et le tour est termine par conception. */
  questions?: NubieAiQuestion[];
  iterations: number;
  plafond_atteint: boolean;
  messages_utilises: number;
  plafond_messages: number;
  alerte_seuil?: number | null;
}

// ============================================
// Pipeline depot -> build -> deploiement (V059)
// ============================================

export interface DeploymentBySource {
  deployment_id: string;
  nom: string;
  environnement_id: string;
  git_branch: string | null;
  connected_repository_id: number | null;
  auto_deploy_enabled: boolean;
  /** Ce deploiement suit-il LE depot demande ? Un deploiement du meme projet peut
   *  suivre un autre depot : l'ecran doit pouvoir le distinguer. */
  suit_ce_depot: boolean;
}

export interface AutoDeployState {
  deployment_id: string;
  auto_deploy_enabled: boolean;
  connected_repository_id: number | null;
  git_branch: string | null;
}

/** Une session OAuth : ce qu'une personne a accorde a un client, une fois.
 *
 * Aucun jeton n'y figure et il n'y en aura jamais : les deux jetons n'ont ete rendus
 * qu'une fois, au client. Il n'y a donc rien a reafficher, seulement a revoquer.
 */
export interface McpGrant {
  id: string;
  /** L'HOTE de l'identifiant du client, jamais le nom qu'il se donne : celui-la est
   *  auto-declare, donc n'importe qui peut y ecrire « Claude ». */
  client_hote: string;
  capacites: McpCapability[];
  cree_le: string;
  vu_le: string | null;
  expire_le: string | null;
  porteur: string | null;
}

/** Ce qu'un client OAuth demande, tel que l'ecran de consentement le montre. */
export interface McpAutorisation {
  client_hote: string;
  portees_demandees: McpCapability[];
  portees_possibles: McpCapability[];
  organisations: { id: string; nom: string }[];
  expire_dans_s: number;
}
